<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue'
import { useRouter } from 'vue-router'
import Button from 'primevue/button'
import Drawer from 'primevue/drawer'
import Popover from 'primevue/popover'
import { useAuthStore } from '@/stores/auth'
import { useNotificationsStore } from '@/stores/notifications'
import { useNotificationsStream } from '@/composables/useNotificationsStream'
import UserAvatar from '@/components/common/UserAvatar.vue'
import SidebarNav from '@/components/layout/SidebarNav.vue'
import NotificationPanel from '@/components/layout/NotificationPanel.vue'

const props = withDefaults(defineProps<{ collapsed?: boolean }>(), { collapsed: false })

const auth = useAuthStore()
const router = useRouter()
const notifications = useNotificationsStore()

const isCollapsed = computed(() => props.collapsed)

// ── User popover (sign out) ──────────────────────────────────────────────────
const userPopover = useTemplateRef<InstanceType<typeof Popover>>('userPopover')
const userOpen = ref(false)

function toggleUserPopover(event: Event): void {
  userPopover.value?.toggle(event)
}

async function handleSignOut(): Promise<void> {
  userPopover.value?.hide()
  await auth.logout()
  router.push({ name: 'login' })
}

// ── Menu popover (global nav: Projects/Tasks/Admin) ──────────────────────────
const menuPopover = useTemplateRef<InstanceType<typeof Popover>>('menuPopover')
const menuOpen = ref(false)

function toggleMenuPopover(event: Event): void {
  menuPopover.value?.toggle(event)
}
function handleMenuNavigate(): void {
  menuPopover.value?.hide()
}

const notifOpen = ref(false)
const notificationTrigger = useTemplateRef<HTMLButtonElement>('notificationTrigger')

function restoreNotificationFocus(): void {
  notificationTrigger.value?.focus({ preventScroll: true })
}

// Live notifications via SSE — the stream emits a `bootstrap` event with
// the initial list on connect, then deltas for created/updated/all-read.
// No HTTP polling.
useNotificationsStream(!auth.isQaQcSpecialist)

const unreadBadge = computed<string | null>(() => {
  const c = notifications.unreadCount
  if (!c) return null
  return c > 99 ? '99+' : String(c)
})
</script>

<template>
  <div class="rail" :class="{ 'rail--collapsed': isCollapsed }">
    <!-- Notifications -->
    <button
      ref="notificationTrigger"
      v-if="!auth.isQaQcSpecialist"
      v-tooltip.right="{ value: isCollapsed ? 'Notifications' : undefined, showDelay: 300 }"
      type="button"
      class="rail__row"
      :aria-expanded="notifOpen"
      aria-haspopup="dialog"
      :aria-label="isCollapsed ? 'Notifications' : undefined"
      data-testid="rail-notifications"
      @click="notifOpen = !notifOpen"
    >
      <span class="rail__icon-wrap">
        <i class="pi pi-bell" aria-hidden="true" />
        <span v-if="unreadBadge" class="rail__badge" aria-live="polite">{{ unreadBadge }}</span>
      </span>
      <span v-if="!isCollapsed" class="rail__label">Notifications</span>
    </button>

    <!-- Menu (global nav) -->
    <button
      v-tooltip.right="{ value: isCollapsed ? 'Menu' : undefined, showDelay: 300 }"
      type="button"
      class="rail__row"
      :aria-expanded="menuOpen"
      aria-haspopup="menu"
      :aria-label="isCollapsed ? 'Open menu' : undefined"
      data-testid="rail-menu"
      @click="toggleMenuPopover"
    >
      <span class="rail__icon-wrap"><i class="pi pi-bars" aria-hidden="true" /></span>
      <span v-if="!isCollapsed" class="rail__label">Menu</span>
    </button>

    <Popover
      ref="menuPopover"
      append-to="body"
      class="menu-popover"
      :dismissable="true"
      @show="menuOpen = true"
      @hide="menuOpen = false"
    >
      <div class="popover-body" role="menu" aria-label="Primary navigation">
        <SidebarNav force-expanded @navigate="handleMenuNavigate" />
      </div>
    </Popover>

    <!-- Settings — a first-class destination in the rail, directly above the
         user identity. RouterLink (not a button) so it highlights when active. -->
    <RouterLink
      v-if="!auth.isQaQcSpecialist"
      v-tooltip.right="{ value: isCollapsed ? 'App settings' : undefined, showDelay: 300 }"
      to="/settings"
      class="rail__row"
      :aria-label="isCollapsed ? 'App settings' : undefined"
      data-testid="rail-settings"
    >
      <span class="rail__icon-wrap"><i class="pi pi-cog" aria-hidden="true" /></span>
      <span v-if="!isCollapsed" class="rail__label">App settings</span>
    </RouterLink>

    <hr v-if="!isCollapsed" class="rail__divider" />

    <!-- User (with sign-out popover) -->
    <button
      v-tooltip.right="{
        value: isCollapsed ? auth.user?.name || auth.user?.email || 'Account' : undefined,
        showDelay: 300,
      }"
      type="button"
      class="rail__user"
      :class="{ 'rail__user--collapsed': isCollapsed }"
      :aria-expanded="userOpen"
      aria-haspopup="dialog"
      :aria-label="isCollapsed ? auth.user?.name || 'Open account menu' : 'Open account menu'"
      data-testid="user-menu-trigger"
      @click="toggleUserPopover"
    >
      <UserAvatar :name="auth.user?.name" :email="auth.user?.email" />
      <template v-if="!isCollapsed">
        <div class="user-meta">
          <div class="user-name">{{ auth.user?.name ?? '' }}</div>
          <div class="user-email">{{ auth.user?.email ?? '' }}</div>
        </div>
        <i class="pi pi-ellipsis-h user-menu-icon" aria-hidden="true" />
      </template>
    </button>

    <Popover
      ref="userPopover"
      append-to="body"
      class="user-popover"
      :dismissable="true"
      @show="userOpen = true"
      @hide="userOpen = false"
    >
      <div class="popover-body" role="menu" aria-label="Account menu">
        <Button
          icon="pi pi-sign-out"
          label="Sign out"
          severity="secondary"
          text
          class="sign-out-button"
          data-testid="sign-out-button"
          @click="handleSignOut"
        />
      </div>
    </Popover>

    <Drawer
      v-if="!auth.isQaQcSpecialist"
      v-model:visible="notifOpen"
      position="right"
      aria-label="Notifications"
      :show-close-icon="false"
      :pt="{ header: { style: { display: 'none' } }, content: { style: { padding: '0' } } }"
      @after-hide="restoreNotificationFocus"
    >
      <NotificationPanel @close="notifOpen = false" />
    </Drawer>
  </div>
</template>

<style scoped>
.rail {
  display: flex;
  flex-direction: column;
  gap: var(--space-0_5);
  border-top: var(--space-px) solid var(--border);
  padding-top: var(--space-2);
  margin-top: var(--space-1);
}

.rail__row {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  padding: var(--nav-item-padding-y) var(--space-3);
  background: transparent;
  border: 0;
  border-radius: var(--radius-md);
  color: var(--text-secondary);
  cursor: pointer;
  font: inherit;
  text-align: left;
  text-decoration: none;
}
.rail__row:hover {
  background: var(--bg-card-hover);
  color: var(--text-primary);
}
.rail__row:focus-visible {
  outline: var(--focus-ring-width) solid var(--green-primary);
  outline-offset: var(--focus-ring-inset);
}
/* Settings rail link (RouterLink) — highlight when the user is on the settings
   route, matching the global nav active state. */
.rail__row.router-link-active {
  background: var(--green-faint);
  color: var(--green-primary);
}

.rail__icon-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--icon-size-nav);
  font-size: var(--font-size-nav);
  flex-shrink: 0;
}

.rail__badge {
  position: absolute;
  top: -6px;
  right: -8px;
  min-width: 16px;
  height: 16px;
  padding: 0 3px;
  background: var(--color-danger-accent);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  border-radius: var(--radius-pill);
  text-align: center;
  pointer-events: none;
}

.rail__label {
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.rail__divider {
  border: 0;
  border-top: var(--space-px) solid var(--border);
  margin: var(--space-1) 0;
}

.rail--collapsed .rail__row {
  justify-content: center;
  padding: var(--nav-item-padding-y) 0;
  gap: 0;
}

.rail__user {
  display: flex;
  align-items: center;
  gap: var(--space-2_5);
  width: 100%;
  padding: var(--space-1_5) var(--space-1_5);
  background: transparent;
  border: 0;
  border-radius: var(--radius-lg);
  cursor: pointer;
  color: inherit;
  font: inherit;
  text-align: left;
  transition: background-color var(--duration-ui) var(--ease-default);
}

.rail__user--collapsed {
  justify-content: center;
  padding: var(--space-1_5) 0;
}
.rail__user:hover {
  background: var(--bg-card-hover);
}
.rail__user:focus-visible {
  outline: var(--focus-ring-width) solid var(--green-primary);
  outline-offset: var(--focus-ring-offset);
}

.user-meta {
  flex: 1;
  min-width: 0;
}
.user-name {
  font-size: var(--font-size-md);
  color: var(--text-primary);
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.user-email {
  font-size: var(--font-size-caption);
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.user-menu-icon {
  flex-shrink: 0;
  color: var(--text-muted);
  font-size: var(--font-size-md);
}

.popover-body {
  min-width: var(--popover-min-w);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-1);
}

.sign-out-button {
  width: 100%;
  justify-content: flex-start;
}
</style>
